import type { Category, LegacyMapping, Scene } from "./types";
import scenesData from "@/data/scenes.json";
import categoriesData from "@/data/categories.json";
import legacyMappingData from "@/data/legacy-mapping.json";

export const scenes = scenesData as Scene[];
export const categories = categoriesData as Category[];
export const legacyMapping = legacyMappingData as LegacyMapping;

export function getScene(slug: string): Scene | undefined {
  return scenes.find((s) => s.slug === slug);
}

export function getCategory(id: string): Category | undefined {
  return categories.find((c) => c.id === id);
}

/** Catégories effectivement représentées dans le prototype */
export function usedCategories(): Category[] {
  const used = new Set(scenes.map((s) => s.category));
  return categories.filter((c) => used.has(c.id));
}

/** GPS valide = dans l'emprise de La Réunion (3 scènes legacy sont à 0,0) */
export function hasGps(s: { latitude: number; longitude: number }): boolean {
  return s.latitude > -21.6 && s.latitude < -20.7 && s.longitude > 55.1 && s.longitude < 56.0;
}

/** Distance en kilomètres entre deux points GPS (haversine) */
export function distanceKm(aLat: number, aLng: number, bLat: number, bLng: number): number {
  const R = 6371;
  const dLat = ((bLat - aLat) * Math.PI) / 180;
  const dLng = ((bLng - aLng) * Math.PI) / 180;
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((aLat * Math.PI) / 180) * Math.cos((bLat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

/** Scènes proches par distance GPS (au sein du prototype) */
export function nearbyScenes(scene: Scene, max = 4): Array<Scene & { distanceKm: number }> {
  if (!hasGps(scene)) return [];
  return scenes
    .filter((s) => s.slug !== scene.slug && hasGps(s))
    .map((s) => ({
      ...s,
      distanceKm: distanceKm(scene.latitude, scene.longitude, s.latitude, s.longitude),
    }))
    .sort((a, b) => a.distanceKm - b.distanceKm)
    .slice(0, max);
}

/** Coordonnées formatées pour affichage (ex. 21°22′ S · 55°33′ E) */
export function formatGps(lat: number, lng: number): string {
  const f = (v: number, pos: string, neg: string) => {
    const abs = Math.abs(v);
    const deg = Math.floor(abs);
    const min = Math.round((abs - deg) * 60);
    return `${deg}°${String(min).padStart(2, "0")}′ ${v >= 0 ? pos : neg}`;
  };
  return `${f(lat, "N", "S")} · ${f(lng, "E", "O")}`;
}
